<template>
  <div id="view-all-tenancies-component-tenants">
    <sticky>
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">
            {{ $i18n.t( 'tenancy.viewAll.title') }}
          </span>
        </div>
        <div class="right-btns">
          <button
            type="button"
            class="btn btn--darkblue"
            @click="resetFilters()"
          >
            {{ $t('action.resetFilter') }}
          </button>
          <button
            id="query-btn"
            type="button"
            class="btn btn--orange"
            @click="queryTenancyRecords()"
          >
            {{ $t('action.search') }}
          </button>
        </div>
      </div>
    </sticky>

    <div class="tenancy-table">
      <div style="display: inline; vertical-align: middle;">
        <span>{{ $t('total') }}: {{ displayListLength }}</span>
      </div>
      <CommonTable
        ref="tenant-tenancy-record-table"
        tableId="tenant-tenancy-record-table"
        :renderHeaderList="itemHeaders"
        :tableContentLists="parsedTenancyRecords"
        :needSearch="true"
        :defaultOrder="true"
        :defaultOrderKey="defaultOrderKey"
        :yScrollAuto="false"
        :minWidth1800px="false"
        :getDisplayListLength="getDisplayListLength"
        :getConditions="getConditions"
        :enterEvent="queryTenancyRecords"
        :interactionFunc="interactionFunc"
        :orderMethods="orderMethods"
        :getTableOrder="getTableOrder"
        :orderByDateKeys="['goExpiredDate']"
      />
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { LIST_NAV_KIND } from '@/constants/listNavigation'
import {
  consumeResumeSearchQueryOnce,
  TENANT_ALL_TENANCY_RECS_LEAVE_PRESERVE_ROUTE_NAMES,
  VIEW_ALL_RESUME_SEARCH_QUERY,
} from '@/constants/viewAllTableCache'
import CommonTable from '@/controls/CommonTable.vue'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Sticky from '@/controls/Sticky'
import viewAllCommonTableSnapshotMixin from '@/mixins/viewAllCommonTableSnapshot.mixin'
import { INPUT_TYPE } from '@/modules/contacts/searchFilter.const'
import mutationTypes from '@/store/mutation-types'
import { DATA_CACHE_KEYS } from '@/store/util'
import dateUtil from '@/utils/dateUtil'
import { FEATURE_NAMES, emptyValue } from '@/utils/consts'
import { getFloorIndex } from '@/api/buildings/request'
import { getTenancyFloors } from '@/api/tenancies/util'
import { MAX_STRING, tenancyType } from '@/utils/locale'
import formFullnameMixin from '../formFullnameMixin'
import { GREEN_OPERATION_SPP_STATUS_OPTIONS } from '../dashboards/AllTenancyRecords.consts'
import filters from '@/filters/filters'

const sortingAttributes = {
  tenancy: {
    type: null,
    status: null,
    premisesDetailList: null,
    premises: null,
    tradeName: null,
  },
  building: {
    fullName: null,
  },
  company: {
    name: null,
  },
  accounts: {
    username: null,
  },
  greenOperation: {
    sppReviewStatus: undefined,
    sppExpiredDate: {
      gte: null,
      lte: null,
    },
  },
}

export default {
  name: 'AllTenancyRecordsTenant',
  /** @see `@/mixins/viewAllCommonTableSnapshot.mixin.js` */
  viewAllTableSnapshotId: 'tenant-tenancy-record-table',
  viewAllTableLeavePreserveRouteNames: TENANT_ALL_TENANCY_RECS_LEAVE_PRESERVE_ROUTE_NAMES,
  viewAllTableOnNonPreserveLeave () {
    this.$store.dispatch('listNavigation/clearListNavigationSequence')
    this.$store.dispatch('listNavigation/clearSuspendedTenancyRecord')
  },
  components: {
    Sticky,
    CommonTable,
  },
  mixins: [formFullnameMixin, viewAllCommonTableSnapshotMixin],
  data () {
    const typeOptions = Object.keys(tenancyType).map(type => { return { label: type, val: tenancyType[`${type}`] } })
    typeOptions.unshift({ label: 'all', val: '' })
    return {
      defaultOrderKey: 'companyName',
      sortColumn: 'companyName',
      sortAscending: true,
      diskCacheKey: DATA_CACHE_KEYS.VIEW_ALL_TR,
      typeOptions,
      greenOperationStatusOptions: GREEN_OPERATION_SPP_STATUS_OPTIONS
        .map((option) => ({ ...option, label: filters.customStatusLabel(option, 'label').toUpperCase() })),
      parsedTenancyRecords: [],
      sortingAttributes: lodash.cloneDeep(sortingAttributes),
      emptyValue,
      interactionFunc: {
        companyName: this.navToPage,
      },
      orderMethods: {
        floor: this.orderByFloor,
        goStatus: this.orderByGoStatus,
        goExpiredDate: this.orderByGoExpiredDate,
      },
      /** Latest grid rows from View All API (not vuex). */
      tenancyRecordRows: [],
      displayListLength: 0,
    }
  },
  created () {
    const q = this.$route.query || {}
    if (String(q[VIEW_ALL_RESUME_SEARCH_QUERY]) !== '1') return
    this.tenancyRecordRows = []
    this.parsedTenancyRecords = []
  },
  methods: {
    ...mapActions('tenancyRecords', ['setLastSearchedAttributes', 'loadAllTenancies']),
    getTableOrder (key, order) {
      this.sortColumn = key
      this.sortAscending = order
    },
    sortColumnTitle (name, label) {
      if (this.sortColumn !== name) {
        return label
      }
      return `${label} ${this.sortAscending ? '\u25B2' : '\u25BC'}`
    },
    customLabel (option, key) {
      const localeKey = key ? option[key] : option
      if (!localeKey || localeKey === emptyValue) return
      return this.$t(`tenancy.${lodash.toLower(localeKey)}`)
    },
    customStatusLabel (option, key) {
      return filters.customStatusLabel(option, key).toUpperCase()
    },
    /** Legacy MultiSelect stored `{ label, val }`; HKL uses primitive (or undefined when empty). */
    normalizeSelectFilterValue (value) {
      if (value == null || value === '') return null
      if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'val')) {
        const legacyVal = value.val
        return legacyVal == null || legacyVal === '' ? null : legacyVal
      }
      return value
    },
    /** Stored null/empty → HKLSelect empty state (undefined, not null — null breaks "All" option value ''). */
    selectFilterToHklValue (stored) {
      const normalized = this.normalizeSelectFilterValue(stored)
      return normalized == null ? undefined : normalized
    },
    hklValueToSelectFilter (hklValue) {
      if (hklValue === undefined || hklValue === null || hklValue === '') return null
      return hklValue
    },
    /** CommonTable filterConditions value for HKLSelect (empty → '' to match All option). */
    selectFilterToCommonTableValue (stored) {
      const normalized = this.normalizeSelectFilterValue(stored)
      return normalized == null ? '' : normalized
    },
    getTenancyRecordNavigationEntries () {
      const table = this.$refs['tenant-tenancy-record-table']
      const rows = table && Array.isArray(table.formatList) ? table.formatList : this.parsedTenancyRecords
      return (rows || []).map(row => ({ id: row.id, kind: LIST_NAV_KIND.TENANCY_RECORD }))
    },
    commitTenancyListNavigationSequence () {
      this.$store.dispatch('listNavigation/clearSuspendedTenancyRecord')
      this.$store.commit(`listNavigation/${mutationTypes.LIST_NAVIGATION_SET_SEQUENCE}`, {
        entries: this.getTenancyRecordNavigationEntries(),
      })
    },
    navToPage (item) {
      this.commitTenancyListNavigationSequence()
      this.$router.push({
        name: 'ccdTenancyTenantView',
        params: { id: item.id },
      })
    },
    filterFloor (item) {
      const filterFloors = deep(this.sortingAttributes, 'tenancy.premisesDetailList')
      if (filterFloors) {
        const filterFloorsArray = filterFloors.split(',')
        if (filterFloors === this.emptyValue) {
          return !item.floor
        } else {
          return filterFloorsArray.every(filterFloor =>
            (item.floorArray || []).find(
              itemFloor => itemFloor.indexOf(filterFloor.trim().toUpperCase()) > -1,
            ),
          )
        }
      }
      return true
    },
    parseRecords () {
      this.parsedTenancyRecords = this.tenancyRecordRows.map((rec) => {
        const building = lodash.get(rec, 'building.code')
        const effectiveFloors = getTenancyFloors(rec, true)
        const floors = lodash.sortBy(effectiveFloors, item => {
          return getFloorIndex(building, item)
        })
        const expiredDate = lodash.get(rec, 'greenOperation.sppExpiredDate')

        return {
          id: rec.id,
          companyName: lodash.get(rec, 'company.name'),
          tradeName: rec.tradeName,
          building: lodash.get(rec, 'building.fullName'),
          floor: lodash.uniq(floors).join(', '),
          floorArray: floors,
          floorIndex: getFloorIndex(building, floors[0]),
          contact: this.getMainContact(rec),
          type: rec.type,
          goStatus: lodash.get(rec, 'greenOperation.sppReviewStatus'),
          goExpiredDate: expiredDate,
          allowLink: true,
        }
      }).filter(rec => this.filterFloor(rec))
    },
    resetFilters () {
      this.sortingAttributes = lodash.cloneDeep(sortingAttributes)
      this.setLastSearchedAttributes({})
      this.$store.dispatch('listNavigation/clearListNavigationSequence')
      this.$store.dispatch('listNavigation/clearSuspendedTenancyRecord')
      const table = this.$refs['tenant-tenancy-record-table']
      if (table) {
        this.$nextTick(() => {
          this.syncTableFilterConditionsFromSortingAttributes()
          table.resetFilterUiOnly()
        })
      }
    },
    /** True when any tenant tenancy-record filter input is effectively set (for resume-search-from-header). */
    checkTenantTenancyRecordsFilterNonEmpty () {
      const s = this.sortingAttributes || {}
      const typeVal = this.normalizeSelectFilterValue(lodash.get(s, 'tenancy.type'))
      const goStatusVal = this.normalizeSelectFilterValue(lodash.get(s, 'greenOperation.sppReviewStatus'))
      return !!(
        (s.company && String(s.company.name || '').trim()) ||
        (s.tenancy && String(s.tenancy.tradeName || '').trim()) ||
        (s.building && String(s.building.fullName || '').trim()) ||
        (s.tenancy && String(s.tenancy.premisesDetailList || '').trim()) ||
        (s.accounts && String(s.accounts.username || '').trim()) ||
        (typeVal !== undefined && typeVal !== null && String(typeVal).trim() !== '') ||
        (goStatusVal !== undefined && goStatusVal !== null && String(goStatusVal).trim() !== '') ||
        lodash.get(s, 'greenOperation.sppExpiredDate.gte')
      )
    },
    async queryTenancyRecords ({ skipDiskCache = false } = {}) {
      const table = this.$refs['tenant-tenancy-record-table']
      if (table && table.filterConditions) {
        this.applyFilterConditionsToSortingAttributes(table.filterConditions)
      }
      this.setLastSearchedAttributes(lodash.cloneDeep(this.sortingAttributes))
      try {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        const rows = await this.loadAllTenancies({
          query: this.getParsedAttributes(),
          diskCacheKey: skipDiskCache ? null : this.diskCacheKey,
        })
        this.tenancyRecordRows = Array.isArray(rows) ? rows : []
        this.parseRecords()
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },
    getDisplayListLength (length) {
      this.displayListLength = length
    },
    getParsedAttributes () {
      let sortingAttributes = lodash.cloneDeep(this.sortingAttributes)
      sortingAttributes.tenancy.type = this.normalizeSelectFilterValue(sortingAttributes.tenancy.type)
      sortingAttributes.greenOperation.sppReviewStatus = this.normalizeSelectFilterValue(
        lodash.get(sortingAttributes, 'greenOperation.sppReviewStatus'),
      )
      const expiredGte = lodash.get(sortingAttributes, 'greenOperation.sppExpiredDate.gte')
      if (expiredGte) {
        sortingAttributes.greenOperation.sppExpiredDate.lte = dateUtil.getEndTimeOfDay(this.sortingAttributes.greenOperation.sppExpiredDate.gte)
      } else {
        sortingAttributes = lodash.omit(sortingAttributes, ['greenOperation.sppExpiredDate'])
      }
      const floor = deep(sortingAttributes, 'tenancy.premisesDetailList')
      if (floor && floor !== null) {
        if (floor === this.$t('emptyValue')) {
          sortingAttributes.tenancy.premisesDetailList = null
        } else {
          sortingAttributes.tenancy.premisesDetailList = `"floor":"${floor}`
        }
      }
      return sortingAttributes
    },
    getMainContact (item) {
      if (item && item.accounts) {
        const adminAccount = item.accounts.find(contact => deep(contact, 'TenancyAccount.isAdmin'))
        if (adminAccount) {
          return this.displayI18nFullName(adminAccount.TenancyAccount.contactFirstName, adminAccount.TenancyAccount.contactLastName)
        }
      }
      return '--'
    },
    applyFilterConditionsToSortingAttributes (rawConditions) {
      this.searchConditionsConvertToQuery(lodash.cloneDeep(rawConditions || {}))
    },
    syncTableFilterConditionsFromSortingAttributes () {
      const table = this.$refs['tenant-tenancy-record-table']
      if (!table) return
      const conditions = {}
      const setInputFilter = (name, value) => {
        if (value === undefined || value === null || value === '') return
        conditions[name] = { value, inputType: INPUT_TYPE.INPUT }
      }
      const setComponentFilter = (name, value) => {
        if (value === undefined || value === null) return
        conditions[name] = {
          value: this.selectFilterToCommonTableValue(value),
          inputType: INPUT_TYPE.COMPONENT,
        }
      }
      setInputFilter('companyName', lodash.get(this.sortingAttributes, 'company.name'))
      setInputFilter('tradeName', lodash.get(this.sortingAttributes, 'tenancy.tradeName'))
      setInputFilter('building', lodash.get(this.sortingAttributes, 'building.fullName'))
      setInputFilter('floor', lodash.get(this.sortingAttributes, 'tenancy.premisesDetailList'))
      setInputFilter('contact', lodash.get(this.sortingAttributes, 'accounts.username'))
      setComponentFilter('type', lodash.get(this.sortingAttributes, 'tenancy.type'))
      setComponentFilter('goStatus', lodash.get(this.sortingAttributes, 'greenOperation.sppReviewStatus'))
      const expiredGte = lodash.get(this.sortingAttributes, 'greenOperation.sppExpiredDate.gte')
      if (expiredGte) {
        conditions.goExpiredDate = {
          value: [expiredGte, null],
          inputType: INPUT_TYPE.DATE,
        }
      }
      const managedKeys = [
        'companyName',
        'tradeName',
        'building',
        'floor',
        'contact',
        'type',
        'goStatus',
        'goExpiredDate',
      ]
      const next = lodash.cloneDeep(table.filterConditions || {})
      managedKeys.forEach((key) => {
        delete next[key]
      })
      table.filterConditions = Object.assign(next, conditions)
    },
    getConditions (conditions) {
      this.applyFilterConditionsToSortingAttributes(conditions)
    },
    searchConditionsConvertToQuery (conditions) {
      this.itemHeaders.forEach((header) => {
        const condition = conditions[header.name]
        if (!condition) return
        const rawValue = typeof condition.value === 'string' ? condition.value.trim() : condition.value
        if (header.name === 'goExpiredDate') {
          this.sortingAttributes[header.queryName[0]][header.queryName[1]].gte = rawValue[0] || null
          return
        }
        if (header.queryName.length !== 2) return
        const value = header.component
          ? this.hklValueToSelectFilter(rawValue)
          : rawValue
        this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
      })
    },
    orderByFloor (item) {
      return item.floorIndex
    },
    orderByGoStatus (item) {
      return item.goStatus || MAX_STRING
    },
    orderByGoExpiredDate (item, order) {
      const lastOne = order ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER
      return item.goExpiredDate ? new Date(item.goExpiredDate).getTime() : lastOne
    },
    normalizeSortingSelectValues () {
      const paths = [
        ['tenancy', 'type'],
        ['greenOperation', 'sppReviewStatus'],
      ]
      paths.forEach((path) => {
        const raw = lodash.get(this.sortingAttributes, path)
        if (raw == null) return
        lodash.set(this.sortingAttributes, path, this.normalizeSelectFilterValue(raw))
      })
    },
  },
  async mounted () {
    $('#search-panel').keydown(function (event) {
      var keypressed = event.keyCode || event.which
      if (keypressed === 13) {
        $('#query-btn').click()
      }
    })
    const { didStripResumeSearch } = await consumeResumeSearchQueryOnce(
      this.$router,
      this.$route.query,
      'tenantAllTenancyRecs',
    )
    const lastSearchAttributes = this.lastSearchedAttributes
    const hasPersistedSearchSnapshot = !lodash.isEmpty(lastSearchAttributes)
    if (didStripResumeSearch && hasPersistedSearchSnapshot) {
      this.sortingAttributes = lodash.cloneDeep(lastSearchAttributes)
      this.normalizeSortingSelectValues()
    }
    await this.$nextTick()
    this.syncTableFilterConditionsFromSortingAttributes()
    if (didStripResumeSearch && hasPersistedSearchSnapshot) {
      await this.queryTenancyRecords()
      await this.$nextTick()
      const table = this.$refs['tenant-tenancy-record-table']
      if (table && typeof table.scrollToLastPosition === 'function') {
        table.scrollToLastPosition()
      }
    }
  },

  computed: {
    ...mapGetters('regions', ['featureStatus']),
    ...mapGetters('tenancyRecords', ['lastSearchedAttributes']),
    hasGreenOperation () {
      return this.featureStatus[FEATURE_NAMES.GREEN_OPERATION]
    },
    typeSelectOptions () {
      return this.typeOptions.map((opt) => ({
        name: this.customLabel(opt, 'label'),
        value: opt.val == null || opt.val === '' ? '' : opt.val,
      }))
    },
    greenOperationSelectOptions () {
      return this.greenOperationStatusOptions.map((opt) => ({
        name: opt.label,
        value: opt.val == null ? '' : opt.val,
      }))
    },
    itemHeaders () {
      return [
        {
          attrPath: 'companyName',
          title: this.sortColumnTitle('companyName', this.$t('tenancy.companyName')),
          name: 'companyName',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['company', 'name'],
        },
        {
          attrPath: 'tradeName',
          title: this.sortColumnTitle('tradeName', this.$t('tenancy.tradeName')),
          name: 'tradeName',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['tenancy', 'tradeName'],
        },
        {
          attrPath: 'building',
          title: this.sortColumnTitle('building', this.$t('tenancy.building')),
          name: 'building',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['building', 'fullName'],
        },
        {
          attrPath: 'floor',
          title: this.sortColumnTitle('floor', this.$t('tenancy.floor')),
          name: 'floor',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['tenancy', 'premisesDetailList'],
        },
        {
          attrPath: 'contact',
          title: this.sortColumnTitle('contact', this.$t('tenancy.mainContact')),
          name: 'contact',
          inputType: INPUT_TYPE.INPUT,
          defaultValue: this.emptyValue,
          queryName: ['accounts', 'username'],
        },
        {
          attrPath: 'type',
          title: this.sortColumnTitle('type', this.$t('tenancy.tenancyType')),
          name: 'type',
          component: HKLSelect,
          options: {
            attributes: {
              options: this.typeSelectOptions,
              showSearch: true,
              placeholder: '',
            },
          },
          defaultValue: this.emptyValue,
          queryName: ['tenancy', 'type'],
          formatRule: (val) => this.customLabel(val),
        },
        this.hasGreenOperation && {
          attrPath: 'goStatus',
          title: this.sortColumnTitle('goStatus', this.$t('table.header.greenOperationStatus')),
          name: 'goStatus',
          component: HKLSelect,
          options: {
            attributes: {
              options: this.greenOperationSelectOptions,
              showSearch: true,
              placeholder: '',
            },
          },
          defaultValue: this.emptyValue,
          queryName: ['greenOperation', 'sppReviewStatus'],
          formatRule: (val) => this.customStatusLabel(val),
        },
        this.hasGreenOperation && {
          attrPath: 'goExpiredDate',
          title: this.sortColumnTitle('goExpiredDate', this.$t('tenancy.greenOperation.expireOn')),
          name: 'goExpiredDate',
          inputType: INPUT_TYPE.DATE,
          singleDatePicker: true,
          datePickerCanPutTotop: false,
          disabledDate: false,
          dateFromPlaceholder: '',
          queryName: ['greenOperation', 'sppExpiredDate'],
          defaultValue: this.emptyValue,
          formatRule: val => (val ? dateUtil.formatDate(val) : this.emptyValue),
        },
      ].filter(Boolean)
    },
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
</style>
<style lang="scss">
#view-all-tenancies-component-tenants {
  .nav-bar {
    max-width: 100%;
    margin: 0;
  }

  .header-panel {
    line-height: 40px;
  }

  .inline-date-picker-block .vdp-datepicker {
    width: 120px !important;
    display: inline-block;
    min-height: 34px;
    vertical-align: bottom;

    input[type='text'] {
      margin: 0;
    }
  }

  .tenancy-table {
    overflow-x: auto;

    .tenant-tenancy-record-table_common-table.common-table {
    display: table !important;
    width: 100% !important;
    margin: 0 !important;
    min-height: unset !important;
    overflow: visible !important;
    border-collapse: collapse;
    border: 1px solid #e0e0e0;

    th,
    td {
      display: table-cell !important;
      box-sizing: border-box;
      vertical-align: middle;
      font: 14px Verdana, sans-serif;
      color: #0c3a58;
    }

    td {
      padding: 10px;
    }

    tr {
      display: table-row !important;
    }

    .common-table-header-container {
      display: table-header-group !important;

      tr {
        border: 1px solid #e0e0e0;
      }

      th {
        background: #f8f8f8;
        padding: 4px 10px;
        height: 55px;
        white-space: normal;
      }

      .common-header-title th {
        font-weight: bold;
        text-transform: uppercase;
        cursor: pointer;
      }

      /* CommonTable scoped caret does not render reliably after legacy table layout overrides. */
      .common-header-title th span > i {
        display: none;
      }

      .common-header-filter th {
        text-transform: uppercase;
        position: relative;

        input[type='text'] {
          height: 40px;
          margin: 0;
          box-sizing: border-box;
        }

        &.type,
        &.goStatus {
          z-index: 2;
        }

        &.type .hkl-select,
        &.goStatus .hkl-select {
          width: 100%;
          max-width: 100%;
        }
      }
    }

    .common-table-content-container {
      display: table-row-group !important;
      overflow: visible !important;

      tr.table-list {
        border: 1px solid #e0e0e0;
        background: #fff;

        &:nth-child(even) {
          background: #f8f8f8;
        }
      }
    }

    /* Legacy 7b5f008: title-row min/fixed widths */
    $title-col-mins: (
      companyName: 170px,
      tradeName: 143px,
      building: 120px,
      floor: 92px,
    );

    @each $col, $width in $title-col-mins {
      .common-header-title th.#{$col} {
        min-width: $width !important;
      }
    }

    .common-header-title th.goStatus {
      width: 180px !important;
      min-width: 180px !important;
    }

    .common-header-title th.goExpiredDate {
      width: 200px !important;
      min-width: 200px !important;
    }

    /* Legacy 7b5f008: filter-row constraints (#search-panel inline + .search-filter-type) */
    .common-header-filter {
      th.building,
      th.floor,
      th.contact {
        max-width: 40px !important;
      }

      th.type {
        min-width: 110px !important;
        max-width: 110px !important;
      }

      th.goStatus {
        max-width: 280px !important;
      }

      th.goExpiredDate {
        max-width: 150px !important;
      }
    }
  }
  }
}
</style>
